import { useHousehold } from "../../app/useHousehold";
import { remPx } from "../sky/common";
import { EmptyState } from "../../ui/StatusBadge";
import type { WidgetDefinition, WidgetProps } from "../types";
import { ArticleLink } from "./ArticleSheet";
import { WidgetHeader } from "../WidgetHeader";
import { FILTER_OPTIONS, filterLabel, mergeStories, NewsImage, SourceBadge, sourcesFor, storyAge, useNewsSources } from "./newsUi";

interface ListConfig {
  /** "all", a category key, or "source:<id>" (newsUi.tsx). */
  filter: string;
  images: boolean;
}

/** Headlines from the household's sources: all of them, one category, or one source. */
function NewsList({ config, size }: WidgetProps<ListConfig>) {
  const { settings, isAdmin } = useHousehold();
  const ids = sourcesFor(config.filter, settings.news.sources);
  const qs = useNewsSources(ids);
  const rows = Math.max(3, Math.floor(size.height / (remPx() * (config.images ? 4.1 : 2.9))));
  const stories = mergeStories(
    qs.map((q) => q.data?.items),
    rows,
  );
  const title = filterLabel(config.filter);

  if (!ids.length)
    return (
      <EmptyState
        icon="news"
        title={`No ${title.toLowerCase()} sources on`}
        detail="Turn sources on in Admin → News & sports, or pick another list in this widget’s settings."
        action={
          isAdmin ? (
            <a className="btn btn-sm btn-primary" href="/admin#news">
              News & sports
            </a>
          ) : undefined
        }
      />
    );

  return (
    <div className="news-list-w">
      <WidgetHeader title={title} meta={ids.length === 1 ? undefined : `${ids.length} sources`} />
      {stories.length === 0 ? (
        qs.some((q) => q.isLoading) ? (
          <div className="widget-loading" />
        ) : (
          <p className="muted">No headlines right now.</p>
        )
      ) : (
        <ul className="news-list">
          {stories.map((s) => {
            const inner = (
              <>
                {config.images && <NewsImage src={s.image} className="news-thumb" />}
                <span className="news-row-text">
                  <span className="news-row-title">{s.title}</span>
                  <span className="news-row-meta">
                    <SourceBadge id={s.source} />
                    {storyAge(s) && <span>{storyAge(s)}</span>}
                  </span>
                </span>
              </>
            );
            return (
              <li key={s.id}>
                <ArticleLink story={s} className="news-row">
                  {inner}
                </ArticleLink>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const newsListWidget: WidgetDefinition<ListConfig> = {
  type: "news-list",
  version: 1,
  name: "Headlines",
  description: "Headlines with pictures from your news sources: all of them, one category or one source.",
  icon: "news",
  category: "household",
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 3 },
  stackHeight: 460,
  defaults: { filter: "all", images: true },
  settings: [
    { key: "filter", label: "Show", type: "select", options: FILTER_OPTIONS },
    { key: "images", label: "Pictures", type: "toggle" },
  ],
  provider: "rss",
  Component: NewsList,
};
